import React from "react";
import { Input } from "./components/ui/input";
import { Button } from "./components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  SearchNavigation,
  SearchSelector,
  SearchTextGeneration,
} from "./components/search";
import {
  SearchElementResult,
  SearchElement,
} from "./components/search/element";
import { ClarificationStep } from "./components/search/clarification-step";
import { experimental_useObject as useObject } from "@ai-sdk/react";
import { ScrollContainer } from "./components/containers/scroll.container";

interface AiSearchProps {
  publicKey: string;
  organizationId: string;
  onSearch?: (query: string) => void;
  theme?: "dark" | "light";
  placeholder?: string;
  maxWidth?: string | number;
}

const searchSchema = z.object({
  query: z
    .string()
    .min(1, "Digite algo para buscar")
    .max(1024, "Busca muito longa"),
});

type SearchFormData = z.infer<typeof searchSchema>;

const dataSchema = z.object({
  needsClarification: z.boolean().optional().describe("Whether clarification is needed from the user."),
  searchTitle: z.string().describe("Title of the search."),
  aiResponse: z.string().describe("Response of the AI."),
  checkboxes: z.array(z.string()).optional().describe("Checkbox options for clarification."),
  checkboxTitle: z.string().optional().describe("Title for the checkbox section."),
  customInputPlaceholder: z.string().optional().describe("Placeholder for custom input field."),
  courses: z.array(
    z.object({
      title: z.string().describe("Title of a course."),
      description: z.string().describe("Description of the course."),
      url: z.string().describe("URL of the course."),
    })
  ),
});

export default function AiSearchComponent({
  onSearch,
  theme = "dark",
  placeholder = "Buscar qualquer coisa...",
  maxWidth,
}: AiSearchProps) {
  const [currentIndex, setCurrentIndex] = React.useState(-1);
  const [isSearching, setIsSearching] = React.useState(false);
  const [hasResults, setHasResults] = React.useState(false);
  const [originalQuery, setOriginalQuery] = React.useState("");
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const resultsRef = React.useRef<HTMLDivElement>(null);

  const { object, submit, isLoading } = useObject({
    api: "https://converflow.api.dev.toolzz.media/search/stream",
    schema: dataSchema,
  });

  console.log(object);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      query: "",
    },
  });

  // Registra o input com ref
  const { ref: registerRef, ...registerRest } = register("query");

  // Gerencia cliques fora do componente
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const path = event.composedPath();
      const rootNode = containerRef.current?.getRootNode();

      if (rootNode instanceof ShadowRoot) {
        const shadowHost = rootNode.host;
        if (path.includes(shadowHost)) {
          return;
        }
      }

      setIsSearching(false);
    };

    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Log quando o estado de busca muda
  React.useEffect(() => {
    console.log("🔎 Estado de busca:", isSearching ? "Aberto" : "Fechado");
  }, [isSearching]);

  // Atualiza o estado de resultados quando o objeto muda
  React.useEffect(() => {
    if (object?.courses?.length || object?.aiResponse || object?.needsClarification) {
      setHasResults(true);
    }
  }, [object]);

  // Scroll para o item selecionado
  const scrollToIndex = React.useCallback((index: number) => {
    if (index === -1 || !resultsRef.current) return;

    const scrollContainer = resultsRef.current.querySelector(
      ".overflow-y-auto"
    ) as HTMLElement;
    const element = scrollContainer?.querySelector(
      `[role="option"][data-result-index="${index}"]`
    ) as HTMLElement;

    if (element && scrollContainer) {
      const elementTop = element.offsetTop;
      const containerHeight = scrollContainer.clientHeight;
      const elementHeight = element.clientHeight;

      // Calcula a posição do scroll para centralizar o elemento
      const newScrollTop = Math.max(
        0,
        elementTop - containerHeight / 2 + elementHeight / 2
      );

      scrollContainer.scrollTo({
        top: newScrollTop,
        behavior: "smooth",
      });
    }
  }, []);

  // Atualiza o scroll quando o índice muda
  React.useEffect(() => {
    scrollToIndex(currentIndex);
  }, [currentIndex, scrollToIndex]);

  const handleNavigateUp = React.useCallback(() => {
    if (!object?.courses?.length) return;
    const courses = object.courses;

    setCurrentIndex((prev) => {
      // Se o índice atual é -1 (nenhum selecionado), começa do último
      if (prev === -1) return courses.length - 1;
      // Se está no primeiro item, vai para o último
      if (prev === 0) return courses.length - 1;
      // Caso contrário, vai para o item anterior
      return prev - 1;
    });
  }, [object?.courses]);

  const handleNavigateDown = React.useCallback(() => {
    if (!object?.courses?.length) return;
    const courses = object.courses;

    setCurrentIndex((prev) => {
      // Se o índice atual é -1 (nenhum selecionado), começa do primeiro
      if (prev === -1) return 0;
      // Se está no último item, volta para o primeiro
      if (prev === courses.length - 1) return 0;
      // Caso contrário, vai para o próximo item
      return prev + 1;
    });
  }, [object?.courses]);

  const handleSelect = React.useCallback(() => {
    if (currentIndex >= 0 && object?.courses?.[currentIndex]) {
      window.open(
        object.courses[currentIndex].url,
        "_blank",
        "noopener,noreferrer"
      );
    }
  }, [currentIndex, object?.courses]);

  // Gerencia teclas de atalho
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      // Se não está buscando e não é Enter, ignora
      if (!isSearching && event.key !== "Enter") return;

      const isInput = event.target === inputRef.current;

      switch (event.key) {
        case "Escape":
          setIsSearching(false);
          inputRef.current?.blur();
          break;
        case "ArrowDown":
        case "ArrowUp": {
          // Sempre previne o comportamento padrão para as setas
          event.preventDefault();
          event.stopPropagation();

          // Se não tem resultados, não faz nada
          if (!object?.courses?.length) return;

          // Executa a navegação
          if (event.key === "ArrowDown") {
            handleNavigateDown();
          } else {
            handleNavigateUp();
          }

          // Se estiver no input, tira o foco dele
          if (isInput) {
            inputRef.current?.blur();
            containerRef.current?.focus();
          }
          break;
        }
        case "Enter":
          // Se estiver no input, deixa o form handle
          if (isInput) return;

          // Se não estiver no input e tiver item selecionado, seleciona o item
          event.preventDefault();
          if (currentIndex >= 0) handleSelect();
          break;
        case "Tab":
          if (isSearching && object?.courses?.length) {
            event.preventDefault();
            if (event.shiftKey) {
              handleNavigateUp();
            } else {
              handleNavigateDown();
            }
            if (isInput) {
              inputRef.current?.blur();
              containerRef.current?.focus();
            }
          }
          break;
      }
    },
    [
      isSearching,
      handleNavigateUp,
      handleNavigateDown,
      currentIndex,
      handleSelect,
      object?.courses?.length,
    ]
  );

  // Gerencia o foco no input
  const handleInputFocus = React.useCallback(() => {
    if (hasResults) {
      setIsSearching(true);
    }
  }, [hasResults]);

  const onSubmit = async (data: SearchFormData) => {
    try {
      setCurrentIndex(-1);
      setIsSearching(true);
      setHasResults(false);
      setOriginalQuery(data.query);

      submit({
        question: data.query,
      });

      onSearch?.(data.query);
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  const handleClarificationSubmit = async (enhancedQuery: string) => {
    try {
      setCurrentIndex(-1);
      setHasResults(false);

      submit({
        question: enhancedQuery,
      });
    } catch (error) {
      console.error("Clarification error:", error);
    }
  };

  return (
    <div
      className={`relative w-full box-border ${
        theme === "light" ? "text-[var(--ai-search-text)]" : "text-white"
      }`}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      ref={containerRef}
      style={{ outline: "none", maxWidth: maxWidth || "650px" }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full space-y-2"
        role="search"
        aria-label="Busca"
      >
        <div className={`group bg-primary-bg flex items-center w-full px-3 py-2 border-1 rounded-2xl transition-all duration-200 border-primary-border`}>
          <Input
            placeholder={placeholder}
            inputSize="default"
            className="pl-0 flex-1 focus:outline-none outline-none"
            {...registerRest}
            ref={(e) => {
              registerRef(e);
              inputRef.current = e;
            }}
            onFocus={handleInputFocus}
            aria-label="Campo de busca"
            aria-invalid={!!errors.query}
            aria-describedby={errors.query ? "search-error" : undefined}
            aria-expanded={isSearching}
            role="combobox"
            aria-controls="search-results"
            aria-haspopup="listbox"
          />
          <Button
            type="submit"
            variant="default"
            size="default"
            className="ml-2"
            disabled={isLoading}
            aria-label={isLoading ? "Buscando..." : "Buscar"}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">Buscando...</span>
            ) : (
              "Buscar"
            )}
          </Button>
        </div>
      </form>

      {isSearching && (
        <div
          id="search-results"
          role="listbox"
          ref={resultsRef}
          className={`absolute w-full mt-2 border-1 rounded-2xl z-10 box-border max-h-[90vh] flex flex-col bg-primary-bg border-primary-border placeholder:text-primary-text-secondary text-primary-text`}
          aria-label="Resultados da busca"
        >
          <SearchSelector theme={theme} />
          <ScrollContainer>
            {object?.needsClarification ? (
              <ClarificationStep
                searchTitle={object.searchTitle || ""}
                checkboxes={object.checkboxes?.filter((item): item is string => typeof item === 'string') || []}
                customInputPlaceholder={object.customInputPlaceholder || "Outra área de interesse"}
                originalQuestion={originalQuery}
                onSubmit={handleClarificationSubmit}
              />
            ) : (
              <>
                <SearchTextGeneration
                  title={object?.searchTitle}
                  response={object?.aiResponse}
                />
                <SearchElementResult
                  elements={object?.courses as SearchElement[] | null}
                  currentIndex={currentIndex}
                  onItemClick={setCurrentIndex}
                  totalCount={object?.courses?.length ?? 0}
                />
              </>
            )}
          </ScrollContainer>
          {!object?.needsClarification && (
            <SearchNavigation
              onNavigateDown={handleNavigateDown}
              onNavigateUp={handleNavigateUp}
              onSelect={handleSelect}
            />
          )}
        </div>
      )}
    </div>
  );
}
